import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AutoInvoiceToggle } from "@/components/billing/AutoInvoiceToggle";

const pad = (n) => String(n).padStart(2, "0");
export const fmtSchedule = (s) => `setiap tanggal ${s.day} pukul ${pad(s.hour)}.${pad(s.minute)} WIB`;
const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
// next_run_at sudah dalam WIB (+07:00); baca komponen langsung agar tidak terpengaruh zona waktu browser.
const fmtNext = (iso) => {
  const [, y, mo, d, h, mi] = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/) || [];
  return y ? `${Number(d)} ${BULAN[Number(mo) - 1]} ${y} ${h}.${mi} WIB` : "—";
};
const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];
const SEL = "h-9 rounded-md border px-2 text-sm bg-white";

const Select = ({ id, value, opts, onChange, fmt = (v) => v }) => (
  <select id={id} data-testid={id} value={value} onChange={(e) => onChange(Number(e.target.value))} className={SEL}>
    {opts.map((o) => <option key={o} value={o}>{fmt(o)}</option>)}
  </select>
);

export const AutoInvoiceSchedule = ({ isAdmin, onLoaded }) => {
  const [sched, setSched] = useState(null);
  const [form, setForm] = useState(null);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/billing/auto-invoice/schedule").then(({ data }) => { setSched(data); onLoaded?.(data); }).catch(() => {});
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAdmin || !sched) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/billing/auto-invoice/schedule", { day: form.day, hour: form.hour, minute: form.minute });
      setSched(data); onLoaded?.(data); setForm(null); setAsk(false);
      toast.success(`Jadwal disimpan: ${fmtSchedule(data)}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal menyimpan jadwal"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border bg-[#F8FAFC] p-2 text-xs space-y-2" data-testid="autoinv-schedule">
      <AutoInvoiceToggle sched={sched} onSaved={(d) => { setSched(d); onLoaded?.(d); }} />
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-0.5">
          <div>Jadwal otomatis: <b data-testid="autoinv-schedule-text">{fmtSchedule(sched)}</b></div>
          {sched.next_run_at && (
            <div className="text-[#4B5563]" data-testid="autoinv-next-run">
              Berikutnya{sched.effective_enabled === false ? " (bila otomatisasi aktif)" : ""}: <b className="font-mono">{fmtNext(sched.next_run_at)}</b>
            </div>
          )}
        </div>
        {!form && <Button size="sm" variant="outline" onClick={() => setForm({ ...sched })} data-testid="autoinv-schedule-edit">Ubah Jadwal</Button>}
      </div>
      {form && (
        <div className="space-y-2" data-testid="autoinv-schedule-form">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1"><Label htmlFor="autoinv-sched-day">Tanggal</Label>
              <Select id="autoinv-sched-day" value={form.day} opts={DAYS} onChange={(day) => { setForm({ ...form, day }); setAsk(false); }} /></div>
            <div className="flex flex-col gap-1"><Label htmlFor="autoinv-sched-hour">Jam</Label>
              <Select id="autoinv-sched-hour" value={form.hour} opts={HOURS} fmt={pad} onChange={(hour) => { setForm({ ...form, hour }); setAsk(false); }} /></div>
            <div className="flex flex-col gap-1"><Label htmlFor="autoinv-sched-minute">Menit</Label>
              <Select id="autoinv-sched-minute" value={form.minute} opts={MINUTES} fmt={pad} onChange={(minute) => { setForm({ ...form, minute }); setAsk(false); }} /></div>
            <span className="pb-2 text-[#6B7280]">Zona waktu: Asia/Jakarta (WIB)</span>
          </div>
          <div className="text-[#6B7280]">Tanggal 1–28 agar valid di semua bulan. Berlaku untuk eksekusi berikutnya; menyimpan tidak menjalankan invoice. Terlambat &gt; 6 jam tidak diproses otomatis (gunakan susulan manual).</div>
          {ask && <div className="text-[#B45309]" data-testid="autoinv-schedule-confirm-text">Ubah jadwal menjadi <b>{fmtSchedule(form)}</b>? Klik Simpan sekali lagi untuk konfirmasi.</div>}
          <div className="flex gap-2">
            <Button size="sm" disabled={busy} onClick={() => (ask ? save() : setAsk(true))} className="bg-[#0D9488] hover:bg-[#0F766E] text-white" data-testid="autoinv-schedule-save">{busy ? "Menyimpan…" : ask ? "Ya, Simpan" : "Simpan"}</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => { setForm(null); setAsk(false); }} data-testid="autoinv-schedule-cancel">Batal</Button>
          </div>
        </div>
      )}
    </div>
  );
};
